// Face detection utility for auto-positioning passport photos.
//
// Uses Transformers.js with the face-parsing model to detect faces and
// calculate proper positioning for passport photo requirements.
//

import type { ImageSegmentationPipeline } from "@huggingface/transformers";

import { PHOTO_SIZES, type PhotoSize } from "./dimensions";

// Face detection result.
//
export interface FaceDetectionResult {
  // Horizontal center of the head (in image pixels).
  //
  headCenterX: number;

  // Top of head position including hair (in image pixels).
  //
  headTop: number;

  // Bottom of chin position (in image pixels).
  // This is used for head height calculation, not hair bottom.
  //
  chinBottom: number;

  // Head height from chin to top of head (in image pixels).
  //
  headHeight: number;

  // Image dimensions.
  //
  imageWidth: number;
  imageHeight: number;
}

// Calculated positioning for passport photo.
//
export interface FacePositioning {
  zoom: number;
  x: number;
  y: number;
}

export interface FacePositioningFrame {
  width: number;
  height: number;
}

// Progress callback for model loading.
//
export type FaceDetectionProgressCallback = (progress: {
  status: string;
  progress?: number;
}) => void;

// Singleton segmenter instance.
//
let segmenter: ImageSegmentationPipeline | null = null;
let isInitializing = false;

// Face detection is always supported with Transformers.js.
//
export function isFaceDetectionSupported(): boolean {
  return true;
}

// Get instructions (not needed with Transformers.js).
//
export function getFaceDetectionInstructions(): string {
  return "Face detection is loading...";
}

// Initialize the face segmentation model.
//
export async function initializeFaceDetection(
  onProgress?: FaceDetectionProgressCallback,
): Promise<void> {
  if (segmenter) return;
  if (isInitializing) {
    // Wait for existing initialization.
    //
    while (isInitializing) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return;
  }

  isInitializing = true;

  try {
    const { env, pipeline } = await import("@huggingface/transformers");
    env.allowLocalModels = false;

    // Only pass progress_callback if defined to satisfy exactOptionalPropertyTypes.
    //
    const options = onProgress ? { progress_callback: onProgress } : {};
    segmenter = await pipeline(
      "image-segmentation",
      "Xenova/face-parsing",
      options,
    );
  } finally {
    isInitializing = false;
  }
}

// Extract bounding box from segmentation mask.
//
// Finds the min/max coordinates of non-zero pixels in the mask.
//
function getBoundingBoxFromMask(
  maskData: Uint8ClampedArray,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } | null {
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let hasPixels = false;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      // Check if pixel is part of the mask (non-zero).
      //
      const value = maskData[idx];
      if (value !== undefined && value > 128) {
        hasPixels = true;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (!hasPixels) return null;

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

// Face-related labels for detecting chin position (bottom of head).
// Excludes hair since hair can hang below the chin.
//
const FACE_LABELS_FOR_CHIN = [
  "skin",
  "skin.png",
  "face",
  "l_eye",
  "r_eye",
  "l_brow",
  "r_brow",
  "nose",
  "u_lip",
  "l_lip",
  "mouth",
];

// Labels for detecting top of head (includes hair).
//
const FACE_LABELS_FOR_TOP = [
  "skin",
  "skin.png",
  "face",
  "l_eye",
  "r_eye",
  "l_brow",
  "r_brow",
  "nose",
  "u_lip",
  "l_lip",
  "mouth",
  "hair",
  "hair.png",
];

// Segment result type from Transformers.js.
//
interface SegmentResult {
  label: string;
  mask?: {
    width: number;
    height: number;
    data: Uint8ClampedArray | ArrayLike<number>;
  };
}

// Head bounds result.
// Note: headTop and chinBottom are the key measurements for passport photos.
//
interface HeadBounds {
  // Horizontal center of the head.
  //
  centerX: number;

  // Top of head (includes hair) - used for positioning.
  //
  headTop: number;

  // Bottom of chin (excludes hanging hair) - used for head height calculation.
  //
  chinBottom: number;

  // Calculated head height from chin to top of head.
  //
  headHeight: number;

  // Mask dimensions for scaling.
  //
  maskWidth: number;
  maskHeight: number;
}

// Calculate head bounds from segmentation results.
//
// For passport photos, head height is measured from chin to top of head.
// - Top of head: includes hair (minimum Y from all face parts including hair)
// - Chin: bottom of face excluding hair (maximum Y from face parts, not hair)
//
function getHeadBounds(results: SegmentResult[]): HeadBounds | null {
  let minX = Infinity;
  let maxX = 0;
  let headTop = Infinity; // Minimum Y (top of head including hair)
  let chinBottom = 0; // Maximum Y from face parts (chin, not hair)
  let foundFace = false;
  let maskWidth = 0;
  let maskHeight = 0;

  for (const result of results) {
    const mask = result.mask;
    if (!mask) continue;

    maskWidth = mask.width;
    maskHeight = mask.height;
    const maskData = new Uint8ClampedArray(mask.data);
    const box = getBoundingBoxFromMask(maskData, maskWidth, maskHeight);
    if (!box) continue;

    // For top of head, include hair.
    //
    if (FACE_LABELS_FOR_TOP.includes(result.label)) {
      headTop = Math.min(headTop, box.y);
      minX = Math.min(minX, box.x);
      maxX = Math.max(maxX, box.x + box.width);
      foundFace = true;
    }

    // For chin (bottom), exclude hair - only use face parts.
    //
    if (FACE_LABELS_FOR_CHIN.includes(result.label)) {
      chinBottom = Math.max(chinBottom, box.y + box.height);
    }
  }

  if (!foundFace || headTop === Infinity || chinBottom === 0) {
    return null;
  }

  return {
    centerX: (minX + maxX) / 2,
    headTop,
    chinBottom,
    headHeight: chinBottom - headTop,
    maskWidth,
    maskHeight,
  };
}

// Detect face in an image using Transformers.js face-parsing.
//
// Returns the detected face bounds or null if no face found.
//
export async function detectFace(
  imageSource: string,
  onProgress?: FaceDetectionProgressCallback,
): Promise<FaceDetectionResult | null> {
  try {
    // Initialize model if needed.
    //
    await initializeFaceDetection(onProgress);

    if (!segmenter) {
      console.error("Face segmenter not initialized");
      return null;
    }

    // Run face segmentation.
    //
    const results = await segmenter(imageSource);

    if (!results || results.length === 0) {
      return null;
    }

    // Get head bounds from segmentation results.
    //
    const headBounds = getHeadBounds(results as SegmentResult[]);
    if (!headBounds) {
      return null;
    }

    // Load original image to get its dimensions.
    //
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = imageSource;
    });

    // Scale measurements to original image dimensions.
    //
    const scaleX = img.naturalWidth / headBounds.maskWidth;
    const scaleY = img.naturalHeight / headBounds.maskHeight;

    return {
      headCenterX: headBounds.centerX * scaleX,
      headTop: headBounds.headTop * scaleY,
      chinBottom: headBounds.chinBottom * scaleY,
      headHeight: headBounds.headHeight * scaleY,
      imageWidth: img.naturalWidth,
      imageHeight: img.naturalHeight,
    };
  } catch (error) {
    console.error("Face detection failed:", error);
    return null;
  }
}

// Calculate optimal positioning for a passport photo based on detected face.
//
// The goal is to scale and position the image so that:
// - Head height (chin to top of head) meets the selected photo requirements
// - Head is roughly centered horizontally
// - Head top has the required margin from the top edge
//
// The image is positioned with CSS:
//   left: 50%, top: 50%
//   transform: translate(calc(-50% + Xpx), calc(-50% + Ypx)) scale(zoom)
//
// When X=0, Y=0, the image is centered in the container.
// Positive X moves right, positive Y moves down.
//
export function calculateFacePositioning(
  face: FaceDetectionResult,
  frame: FacePositioningFrame,
  photoSize: PhotoSize,
): FacePositioning {
  const { headCenterX, headTop, headHeight, imageWidth, imageHeight } = face;
  const headRequirements = PHOTO_SIZES[photoSize].head;

  // Target head height: aim for the middle of the acceptable range.
  //
  const targetHeadPercent =
    (headRequirements.minPercent + headRequirements.maxPercent) / 2;
  const targetHeadHeight = frame.height * (targetHeadPercent / 100);

  // Calculate zoom to achieve target head height.
  // headHeight is chin-to-top measurement from face detection.
  //
  const zoom = Math.max(0.1, Math.min(5, targetHeadHeight / headHeight));

  // Image center in original coordinates.
  //
  const imageCenterX = imageWidth / 2;
  const imageCenterY = imageHeight / 2;

  // Horizontal offset: center the head in the container.
  // When X=0, image center aligns with container center.
  // We need to shift so head center aligns with container center.
  //
  const headOffsetX = headCenterX - imageCenterX;
  const x = -headOffsetX * zoom;

  // Vertical offset: position the head top at the profile's target margin.
  //
  const targetHeadTopY =
    frame.height * (headRequirements.targetTopPercent / 100);

  // Head top offset from image center (in original pixels).
  //
  const headTopOffsetFromImageCenter = headTop - imageCenterY;

  // After zoom with Y=0, head top would be at:
  //   containerCenter + headTopOffsetFromImageCenter * zoom
  // We want it at targetHeadTopY, so:
  //   Y = targetHeadTopY - containerCenter - headTopOffsetFromImageCenter * zoom
  //
  const containerCenter = frame.height / 2;
  const y =
    targetHeadTopY - containerCenter - headTopOffsetFromImageCenter * zoom;

  return {
    zoom,
    x,
    y,
  };
}
